import { QueryCache, QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { removeOldestQuery } from "@tanstack/react-query-persist-client";
import { AxiosError } from "axios";
import type { Persister } from "@tanstack/react-query-persist-client";

// Bump this whenever the shape of the cached data changes, it discards stale caches on the clients
const CACHE_BUSTER = "1";
const CACHE_KEY = "kpcs-reports-query-cache";
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const isAuthIssue = (error: unknown): boolean => {
    return (error as AxiosError)?.response?.status === 401;
};

export const getErrorMessage = (error: unknown): string => {
    if (!error) { return ""; }
    if (isAuthIssue(error)) { return "Přístupový token vypršel."; }
    return (error as AxiosError).message;
};

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error: unknown) => console.log((error as AxiosError)?.response?.data)
    }),
    defaultOptions: {
        queries: {
            // Cached data is rendered immediately and refreshed on the background.
            // Within this window switching between the pages does not hit the API again,
            // a change made in the app invalidates the affected data right away.
            staleTime: 60 * 1000,
            cacheTime: DAY_IN_MS,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            retry: (failureCount: number, error: unknown) => !isAuthIssue(error) && failureCount < 2
        },
        mutations: {
            retry: false
        }
    }
});

const storagePersister = createSyncStoragePersister({
    storage: window.localStorage,
    key: CACHE_KEY,
    throttleTime: 1000,
    // Drops the least recently used entries instead of failing when the storage is full
    retry: removeOldestQuery
});

// Data used to be cached under these keys before the query cache took over
const removeLegacyCache = () => {
    const legacyKeys = [
        "me", "reports", "projects", "projectswithdetails", "customers",
        "documents", "tasks", "dashboard", "dashboard-month"
    ];

    legacyKeys.forEach((key: string) => window.localStorage.removeItem(key));
};

removeLegacyCache();

// Teams dialogs run in their own browser context but share the storage with the main window.
// They may only read the cache, otherwise they would overwrite what the main window has loaded.
const readOnlyPersister: Persister = {
    persistClient: () => undefined,
    restoreClient: () => storagePersister.restoreClient(),
    removeClient: () => undefined
};

export const getPersistOptions = (readOnly: boolean) => ({
    persister: readOnly ? readOnlyPersister : storagePersister,
    buster: CACHE_BUSTER,
    maxAge: DAY_IN_MS
});
